import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, LocateFixed, MapPin, Search, Star, Store } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { use_geolocation } from '@/lib/use-geolocation';
import { AuthImage } from '@/components/auth-image';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { SectionHeader } from '@/components/ui/section-header';
import { SelectField } from '@/components/ui/select';
import { use_vendor_discovery } from '@/features/discovery/hooks';
import { DiningChips } from '@/components/dining-capability-chips';
import {
  DISCOVERY_MODULES,
  MODULE_LABEL,
  type DiscoveryListItem,
  type VendorModuleType,
} from '@/features/discovery/types';
import { ProviderAvatar, is_at_your_door } from '@/components/provider-avatar';
import { CREMATION_SERVICE_OPTIONS, type CremationServiceType } from '@/features/cremation/types';
import { LAB_SAMPLE_TYPE_OPTIONS, type LabSampleType } from '@/features/lab/types';

function VendorCard({ v, onClick }: { v: DiscoveryListItem; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`vendor-card-${v.id}`}
      className="group clay-press flex w-full flex-col overflow-hidden rounded-card border-2 border-brand-outline-variant/20 bg-card text-left shadow-clay transition-all duration-normal ease-bounce hover:-translate-y-1 hover:border-primary/40 hover:shadow-clay-lg"
    >
      {/* Hero */}
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
        {v.cover_photo_path ? (
          <AuthImage
            path={`${v.cover_photo_path}?variant=medium`}
            alt={v.business_name ?? 'Vendor'}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center accent-gradient text-white/90">
            <Store className="h-10 w-10" />
          </div>
        )}
        {/* Rating chip */}
        {v.review_count > 0 && (
          <span className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-xs font-semibold text-white backdrop-blur-sm">
            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
            {v.avg_rating.toFixed(1)}
          </span>
        )}
        {v.is_24x7 && (
          <span className="absolute left-2 top-2 rounded-full bg-primary/90 px-2 py-0.5 text-[11px] font-semibold text-primary-foreground backdrop-blur-sm">
            24×7
          </span>
        )}
        {v.distance_km != null && (
          <span
            className="absolute bottom-2 left-2 z-10 flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-semibold text-foreground shadow-clay-sm backdrop-blur-sm"
            data-testid={`vendor-distance-${v.id}`}
          >
            <MapPin className="h-3 w-3 text-primary" /> {v.distance_km.toFixed(1)} km
          </span>
        )}
        {/* Name + city over a gradient scrim */}
        <div className="absolute inset-x-0 bottom-0 flex items-end gap-2 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8">
          {/*
            For a walker or trainer the vendor IS a person, so their face rides on the card as
            well as on the pages below it — a shopper scanning a list of walkers is choosing a
            human, and the hero shot is whatever premises photo happened to rank highest.
            Ringed in white because it sits on a photo, not a flat surface.
          */}
          {is_at_your_door(v.module_types) && (
            <ProviderAvatar
              path={v.profile_photo_path}
              name={v.business_name}
              size="sm"
              data-testid={`vendor-avatar-${v.id}`}
            />
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate font-heading text-base font-bold text-white">
              {v.business_name ?? 'Unnamed'}
            </p>
            {v.city && (
              <p className="flex items-center gap-1 truncate text-xs text-white/85">
                <MapPin className="h-3 w-3" /> {v.city}
                {v.state ? `, ${v.state}` : ''}
              </p>
            )}
          </div>
        </div>
      </div>
      {/* Module badges */}
      <div className="flex flex-wrap gap-1 p-3 pb-0">
        {v.module_types.map((m) => (
          <span key={m} className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold text-secondary-foreground">
            {MODULE_LABEL[m] ?? m}
          </span>
        ))}
      </div>
      {v.dining && <DiningChips dining={v.dining} className="p-3 pt-2" />}
    </button>
  );
}

function CardSkeleton() {
  return (
    <div className="overflow-hidden rounded-card border-2 border-brand-outline-variant/20 bg-card shadow-clay">
      <div className="aspect-[16/10] w-full animate-pulse bg-muted" />
      <div className="flex gap-1 p-3">
        <div className="h-4 w-14 animate-pulse rounded-full bg-muted" />
        <div className="h-4 w-12 animate-pulse rounded-full bg-muted" />
      </div>
    </div>
  );
}

export function DiscoverPage() {
  const navigate = useNavigate();
  const [module, set_module] = useState<VendorModuleType | undefined>(undefined);
  const [q_input, set_q_input] = useState('');
  const [q, set_q] = useState('');
  const [city, set_city] = useState('');

  // CREMATION-only filters.
  const [home_pickup, set_home_pickup] = useState(false);
  const [emergency, set_emergency] = useState(false);
  const [cremation_type, set_cremation_type] = useState<CremationServiceType | ''>('');

  // LAB-only filters.
  const [home_collection, set_home_collection] = useState(false);
  const [lab_sample_type, set_lab_sample_type] = useState<LabSampleType | ''>('');

  const geo = use_geolocation();
  // At-your-location modules rank nearest-first: walkers, (home-visit) trainers, cremation, labs,
  // and groomers (at-salon, ranked nearest-first but never excluded by radius).
  const is_nearest =
    module === 'WALKER' || module === 'TRAINER' || module === 'CREMATION' || module === 'LAB' || module === 'GROOMER';
  const is_cremation = module === 'CREMATION';
  const is_lab = module === 'LAB';
  // Nearest-first only when such a filter is active AND coords are granted.
  const use_distance = is_nearest && geo.status === 'granted' && geo.lat != null && geo.lng != null;

  // Debounce the free-text query so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => set_q(q_input.trim()), 300);
    return () => clearTimeout(t);
  }, [q_input]);

  // Auto-request location the first time a nearest-first filter is chosen.
  const geo_status = geo.status;
  const geo_request = geo.request;
  useEffect(() => {
    if (is_nearest && geo_status === 'idle') geo_request();
  }, [is_nearest, geo_status, geo_request]);

  const list_q = use_vendor_discovery({
    module,
    q: q || undefined,
    city: city.trim() || undefined,
    lat: use_distance ? geo.lat : undefined,
    lng: use_distance ? geo.lng : undefined,
    home_pickup: is_cremation && home_pickup ? true : undefined,
    emergency: is_cremation && emergency ? true : undefined,
    cremation_service_type: is_cremation && cremation_type ? cremation_type : undefined,
    home_collection: is_lab && home_collection ? true : undefined,
    lab_sample_type: is_lab && lab_sample_type ? lab_sample_type : undefined,
  });

  const items = list_q.data?.items ?? [];

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader title="Find services" description="Browse verified providers near you." />

      {/* Module chips */}
      <div className="flex flex-wrap gap-2" data-testid="module-chips">
        <button
          type="button"
          onClick={() => set_module(undefined)}
          data-testid="module-chip-all"
          data-active={module === undefined}
          className={
            'rounded-full border-2 px-3 py-1.5 text-sm font-semibold transition-all duration-fast ' +
            (module === undefined
              ? 'border-primary bg-primary/10 text-primary shadow-clay-sm'
              : 'border-brand-outline-variant/20 bg-card text-muted-foreground hover:border-primary/30 hover:text-foreground')
          }
        >
          All
        </button>
        {DISCOVERY_MODULES.map((m) => (
          <button
            key={m.value}
            type="button"
            onClick={() => set_module(m.value)}
            data-testid={`module-chip-${m.value}`}
            data-active={module === m.value}
            className={
              'rounded-full border-2 px-3 py-1.5 text-sm font-semibold transition-all duration-fast ' +
              (module === m.value
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-input bg-card text-foreground hover:bg-muted')
            }
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Search + city */}
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search by name…"
            value={q_input}
            onChange={(e) => set_q_input(e.target.value)}
            data-testid="discovery-search"
          />
        </div>
        <Input
          className="sm:w-48"
          placeholder="City"
          value={city}
          onChange={(e) => set_city(e.target.value)}
          data-testid="discovery-city"
        />
      </div>

      {/* Nearest-first location banner (walkers / trainers) — needs browser geolocation. */}
      {is_nearest && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-card border-2 border-brand-outline-variant/20 bg-card px-3 py-2 text-sm shadow-clay-sm"
          data-testid="walker-geo-banner"
        >
          {geo.status === 'prompting' ? (
            <span className="flex items-center gap-2 text-muted-foreground" data-testid="geo-prompting">
              <Loader2 className="h-4 w-4 animate-spin" /> Finding your location…
            </span>
          ) : use_distance ? (
            <span className="flex items-center gap-2 font-medium text-primary" data-testid="geo-granted">
              <LocateFixed className="h-4 w-4" /> Showing providers nearest to you first.
            </span>
          ) : (
            <>
              <span className="flex items-center gap-2 text-muted-foreground" data-testid="geo-denied">
                <MapPin className="h-4 w-4" />
                {geo.status === 'unavailable'
                  ? 'Location unavailable — showing the city list. '
                  : 'Enable location to see the nearest providers first. '}
              </span>
              <Button size="sm" variant="outline" onClick={geo.request} data-testid="geo-enable">
                <LocateFixed className="h-4 w-4" /> Use my location
              </Button>
            </>
          )}
        </div>
      )}

      {/* Cremation-specific filters */}
      {is_cremation && (
        <div className="flex flex-wrap items-center gap-2" data-testid="cremation-filters">
          <button
            type="button"
            onClick={() => set_home_pickup((v) => !v)}
            data-testid="cremation-filter-home-pickup"
            data-active={home_pickup}
            className={
              'rounded-full border-2 px-3 py-1.5 text-sm font-semibold transition-all duration-fast ' +
              (home_pickup
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-input bg-card text-foreground hover:bg-muted')
            }
          >
            Home pickup
          </button>
          <button
            type="button"
            onClick={() => set_emergency((v) => !v)}
            data-testid="cremation-filter-emergency"
            data-active={emergency}
            className={
              'rounded-full border-2 px-3 py-1.5 text-sm font-semibold transition-all duration-fast ' +
              (emergency
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-input bg-card text-foreground hover:bg-muted')
            }
          >
            24×7 emergency
          </button>
          <SelectField
            value={cremation_type}
            onValueChange={(v) => set_cremation_type(v as CremationServiceType)}
            placeholder="Any cremation type"
            data-testid="cremation-filter-type"
            className="w-52"
            options={[{ value: '', label: 'Any cremation type' }, ...CREMATION_SERVICE_OPTIONS]}
          />
        </div>
      )}

      {/* Lab-specific filters */}
      {is_lab && (
        <div className="flex flex-wrap items-center gap-2" data-testid="lab-filters">
          <button
            type="button"
            onClick={() => set_home_collection((v) => !v)}
            data-testid="lab-filter-home-collection"
            data-active={home_collection}
            className={
              'rounded-full border-2 px-3 py-1.5 text-sm font-semibold transition-all duration-fast ' +
              (home_collection
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-input bg-card text-foreground hover:bg-muted')
            }
          >
            Home collection
          </button>
          <SelectField
            value={lab_sample_type}
            onValueChange={(v) => set_lab_sample_type(v as LabSampleType)}
            placeholder="Any sample type"
            data-testid="lab-filter-sample-type"
            className="w-52"
            options={[{ value: '', label: 'Any sample type' }, ...LAB_SAMPLE_TYPE_OPTIONS]}
          />
        </div>
      )}

      {/* Results */}
      {list_q.isLoading ? (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="discovery-loading">
          {Array.from({ length: 4 }).map((_, i) => (
            <li key={i}>
              <CardSkeleton />
            </li>
          ))}
        </ul>
      ) : list_q.isError ? (
        <ErrorState
          data-testid="discovery-error"
          title="Couldn't load vendors"
          description="Please try again."
        />
      ) : items.length === 0 ? (
        <EmptyState
          data-testid="discovery-empty"
          icon={Store}
          title="No vendors found"
          description="Try a different filter or search term."
        />
      ) : (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="vendor-list">
          {items.map((v) => (
            <li key={v.id}>
              <VendorCard v={v} onClick={() => navigate(`/app/discover/${v.id}`)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
